'use client';

import { useState, useEffect } from 'react';
// Remove Image import if no longer used directly here
// import Image from 'next/image';
// Remove SpiderChart import if only used within WelcomeStep
// import SpiderChart from './components/SpiderChart';
import { useRouter } from 'next/navigation';
import { AnimatePresence } from 'framer-motion';

// Import the new components
import WelcomeStep from './components/WelcomeStep';
import AssessmentStep from './components/AssessmentStep';
import { questions } from './data/questions';
import { dimensions } from './data/benchmarks';
import ResultDisplay from './components/ResultDisplay';

// Keep data definitions here (or move to data file later if preferred)
// Define case studies only once - NOTE: This seems unused now, could potentially remove?
// const caseStudiesArray = [ ... ];

// Define the structure of a question object for type safety
// interface Question { ... }

// Restore the full questions array in the correct order
// const questions: Question[] = [ ... ];

const SESSION_STORAGE_STEP_KEY = 'assessmentStep';
const SESSION_STORAGE_QUESTION_KEY = 'assessmentCurrentQuestion';
const SESSION_STORAGE_ANSWERS_KEY = 'assessmentAnswers';

export default function BenchmarkPage() {
  const router = useRouter();

  // Re-introduce state with initial values potentially read from session storage
  const [step, setStep] = useState<'welcome' | 'assessment'>('welcome');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [questionTimes, setQuestionTimes] = useState<number[]>([]);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [isInitialized, setIsInitialized] = useState(false); // Flag to prevent premature storage write

  // Effect to initialize state from session storage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        // Check if we're returning from results page FIRST
        const assessmentCompleted = sessionStorage.getItem('assessmentCompleted');
        if (assessmentCompleted === 'true') {
          // Clear all assessment state when returning from results
          clearAssessmentState();
          setStep('welcome');
          setCurrentQuestion(0);
          setAnswers([]);
          setQuestionTimes([]);
          
          // Clear the completed flag to avoid infinite loops
          sessionStorage.removeItem('assessmentCompleted');
          setIsInitialized(true);
          return; // Exit early, don't load stored state
        }

        // Only load stored state if we're not coming from results page
        // Retrieve step from session storage
        const storedStep = sessionStorage.getItem(SESSION_STORAGE_STEP_KEY);
        if (storedStep) {
          setStep(storedStep as 'welcome' | 'assessment');
        }

        // Retrieve current question from session storage
        const storedQuestion = sessionStorage.getItem(SESSION_STORAGE_QUESTION_KEY);
        if (storedQuestion) {
          setCurrentQuestion(parseInt(storedQuestion, 10));
        }

        // Retrieve answers from session storage
        const storedAnswers = sessionStorage.getItem(SESSION_STORAGE_ANSWERS_KEY);
        if (storedAnswers) {
          setAnswers(JSON.parse(storedAnswers));
        }
      } catch (error) {
        console.error('Error retrieving session storage:', error);
      }
    }
    setIsInitialized(true); // Mark initialization complete
  }, []); // Remove step from dependency array

  useEffect(() => {
    // Reset the start time whenever a new question is shown
    setQuestionStartTime(Date.now());
  }, [currentQuestion]);

  // Effect to save state to session storage when it changes (after initialization)
  useEffect(() => {
    if (!isInitialized) return; // Don't save until initialized

    try {
      if (step === 'assessment') {
        sessionStorage.setItem(SESSION_STORAGE_STEP_KEY, step);
        sessionStorage.setItem(SESSION_STORAGE_QUESTION_KEY, currentQuestion.toString());
        sessionStorage.setItem(SESSION_STORAGE_ANSWERS_KEY, JSON.stringify(answers));
      } else {
        // Clear storage if back on welcome step (handled by handleBack/handleStart)
        // We might not need to clear here if other handlers manage it
      }
    } catch (error) {
      console.error('Error writing to session storage:', error);
    }
  }, [step, currentQuestion, answers, isInitialized]);

  // Clear storage helper function
  const clearAssessmentState = () => {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_STEP_KEY);
      sessionStorage.removeItem(SESSION_STORAGE_QUESTION_KEY);
      sessionStorage.removeItem(SESSION_STORAGE_ANSWERS_KEY);
    } catch (error) {
      console.error('Error clearing session storage:', error);
    }
  };

  // Re-introduce handlers
  const handleAnswer = (answer: number) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);

    const end = Date.now();
    const newTimes = [...questionTimes, Math.round((end - questionStartTime) / 1000)];
    setQuestionTimes(newTimes);

    sessionStorage.setItem('assessmentAnswers', JSON.stringify(newAnswers));
    sessionStorage.setItem('assessmentTimes', JSON.stringify(newTimes));

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Assessment is complete, set flag and navigate
      sessionStorage.setItem('assessmentCompleted', 'true');
      router.push('/benchmark/results');
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setAnswers(answers.slice(0, -1)); // Remove last answer
    } else {
      // Go back to welcome, clear state and storage
      setStep('welcome');
      setAnswers([]);
      setCurrentQuestion(0); // Ensure question index is reset
      clearAssessmentState();
    }
  };

  const handleStartAssessment = () => {
    // Clear all state first
    setAnswers([]);
    setCurrentQuestion(0);
    setQuestionTimes([]);
    
    // Clear session storage
    clearAssessmentState();
    
    // Set step to assessment
    setStep('assessment');
  };

  // Restore animation config
  const pageVariants = {
    initial: { opacity: 0 },
    in: { opacity: 1 },
    out: { opacity: 0 },
  };

  const pageTransition = {
    type: 'tween',
    ease: 'anticipate',
    duration: 0.5,
  };

  // Don't render until initialization check is complete
  if (!isInitialized) {
    return null; // Or a loading indicator
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-gray-850 rounded-lg p-8 border border-gray-800">
          {/* Restore AnimatePresence wrapper */}
          <AnimatePresence mode="wait">
            {/* Restore animation props */}
            {step === 'welcome' && (
              <WelcomeStep
                key="welcome" // Add key back
                onStartAssessment={handleStartAssessment}
                pageVariants={pageVariants}
                pageTransition={pageTransition}
              />
            )}
            {step === 'assessment' && (
              <AssessmentStep
                key="assessment" // Add key back
                currentQuestion={currentQuestion}
                questions={questions}
                dimensions={dimensions}
                onAnswer={handleAnswer}
                onBack={handleBack}
                pageVariants={pageVariants}
                pageTransition={pageTransition}
              />
            )}
          </AnimatePresence>
          {/* Only show results after assessment is complete (not on welcome or assessment steps) */}
          {step !== 'welcome' && step !== 'assessment' && (
            <ResultDisplay
              initialAnswers={answers}
              initialTimes={questionTimes}
            />
          )}
        </div>
      </div>
    </div>
  );
}
